import { useEffect, useRef } from 'react';
import type { DrawerProps } from '@mui/material';
import { useFormContext, type FieldPath } from 'react-hook-form';
import { isNotEmptyString, withDefault } from '@vy/utils';
import { cloneDeep } from 'lodash';
import { userMessage } from '../../../../../../utils/vy-toasts';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';

type ModifyPanelProduct = { id?: string; variants: { name?: string }[] };
type ProductArrayName = 'fixedProducts' | 'usageProducts';

type UseModifyPanelParams<TProduct extends ModifyPanelProduct> = {
  arrayName: ProductArrayName;
  product: TProduct;
  open: DrawerProps['open'];
  onClose: DrawerProps['onClose'];
  /** Skip the save-time validation gate (e.g. refunds validate on the page, not in the drawer). */
  shouldBypassValidationGate?: (product: TProduct) => boolean;
};

/**
 * Shared drawer wiring for the Fixed/Usage modify panels: locate the product in
 * the form array, build the title, snapshot-on-open for cancel-to-original, and
 * the close/cancel/save handlers.
 */
export function useModifyPanel<TProduct extends ModifyPanelProduct>({
  arrayName,
  product,
  open,
  onClose,
  shouldBypassValidationGate,
}: UseModifyPanelParams<TProduct>) {
  const { getValues, setValue, trigger } = useFormContext<PlanInputForm, NewPlanForm>();
  const products = withDefault(getValues(arrayName), []) as ModifyPanelProduct[];
  const productIndex = products.findIndex((candidate) => candidate.id === product.id);
  // The array name is dynamic, so the path can't be inferred to a single literal.
  const productPath = `${arrayName}.${productIndex}` as FieldPath<PlanInputForm>;

  const { name } = product.variants.at(0) ?? {};
  const drawerTitle = `Edit ${isNotEmptyString(name) ? name : `Product #${productIndex + 1}`}`;

  const initialProductConfig = useRef(cloneDeep(getValues(productPath)));

  // Snapshot the product config when the drawer opens so Cancel can revert to it.
  // Keyed only on `open` on purpose — re-running on every form change would
  // overwrite the baseline and break cancel-to-original.
  useEffect(() => {
    if (open) {
      initialProductConfig.current = cloneDeep(getValues(productPath));
    }
  }, [open]);

  const handleClose = () => {
    onClose?.({}, 'backdropClick');
  };

  const handleCancel = () => {
    setValue(productPath, initialProductConfig.current);
    handleClose();
  };

  const handleSave = async () => {
    const isProductValid = await trigger(productPath);

    if (!isProductValid && !shouldBypassValidationGate?.(product)) {
      userMessage('warning', 'Please fix the highlighted fields before saving');
      return;
    }

    handleClose();
  };

  return { productIndex, drawerTitle, handleClose, handleCancel, handleSave };
}
